import { debounce } from 'lodash-es'
import axios, { AxiosError } from 'axios'
import { getVersionConfig } from './preRequest'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'
import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  RequestConfig,
  AxiosResponse
} from './types'
// import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage, ElLoading } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'

// export const PATH_URL = import.meta.env.VITE_APP_BASE_API + '/api'
export const PATH_URL = import.meta.env.VITE_APP_BASE_API + '/bi-manage'

// export const PATH_URL = ''

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

let loadingDom: any = null

axiosInstance.interceptors.request.use(async (res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(url, controller)
  // 只有没有设置 hideLoading 的接口才显示 loading
  const config = res as RequestConfig
  if (!config?.hideLoading) {
    loadingDom = ElLoading.service({ fullscreen: true, background: 'rgba(0,0,0,0)' })
  }
  try {
    const versionData = await getVersionConfig()
    if (versionData.url) {
      res.headers.versionDay = versionData.versionDay
    }
  } catch (e) {
    console.log(e)
  }
  return res
})

const showTimeoutError = debounce(
  (msg) => {
    ElMessage.error(msg)
  },
  REQUEST_TIMEOUT,
  { leading: true, trailing: false }
)

const NETWORK_ERROR = 5000
const showNetworkError = debounce(
  (msg) => {
    ElMessage.error(msg)
  },
  NETWORK_ERROR,
  { leading: true, trailing: false }
)

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    // 只有没有设置 hideLoading 的接口才关闭 loading
    const config = res.config as RequestConfig
    if (!config?.hideLoading) {
      loadingDom?.close()
    }
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res
  },
  (error: AxiosError) => {
    // 只有没有设置 hideLoading 的接口才关闭 loading
    const config = error.config as RequestConfig
    if (!config?.hideLoading) {
      loadingDom?.close()
    }
    console.log('err： ' + error) // for debug
    const isCn = error.config?.headers['local-language'] === 'zh-CN'
    if (error.message === 'Network Error') {
      const networkErrorMsg = isCn
        ? '网络错误，请检查网络'
        : 'network error, please check the network'
      showNetworkError(networkErrorMsg)
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const requestTimeoutMsg = isCn ? '连接超时，请重试' : 'connection timed out, please try again'
      showTimeoutError(requestTimeoutMsg)
    } else {
      console.log('else', error.message)
      ElMessage.error(error.message)
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
